<template>
  <form-group-thux-horizontal
    :validator="validator"
    :label="labelForm | translate"
    :label-class="labelFormClass"
    :label-cols-sm="labelColsSm"
    :label-cols-lg="labelColsLg"
    :class="classForm"
    :external-errors="externalErrors"
    :disabled="disabled"
    :description="description"
  >
    <template slot-scope="{ attrs, listeners }">
      <div
        class="color-picker-thux"
        :class="{ 'is-disabled': disabled, 'is-empty': !isValidHex(model) }"
      >
        <button
          type="button"
          :id="triggerId"
          ref="trigger"
          class="color-picker-thux__swatch"
          :style="swatchStyle"
          :disabled="disabled"
          :aria-label="'Pick a color' | translate"
          @click.prevent="togglePopover"
        >
          <span class="color-picker-thux__swatch-ring" aria-hidden="true"></span>
          <i
            v-if="!isValidHex(model)"
            class="fas fa-eye-dropper color-picker-thux__swatch-hint"
            aria-hidden="true"
          ></i>
        </button>

        <div class="color-picker-thux__input-wrap">
          <b-form-input
            v-bind="attrs"
            v-on="listeners"
            v-model="model"
            type="text"
            class="color-picker-thux__hex"
            :placeholder="placeholderInput || '#RRGGBB'"
            :disabled="disabled"
            :formatter="formatHex"
            autocomplete="off"
            maxlength="7"
            spellcheck="false"
          />
          <button
            v-if="isValidHex(model) && !disabled"
            type="button"
            class="color-picker-thux__clear"
            :title="'Clear' | translate"
            @click.prevent="clear"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <b-popover
          :target="triggerId"
          :show.sync="popoverShown"
          triggers="manual"
          placement="bottom"
          custom-class="color-picker-thux__popover"
          :disabled="disabled"
          boundary="window"
        >
          <div class="color-picker-thux__panel" @mousedown.stop>
            <chrome-picker
              :value="pickerValue"
              :disable-alpha="true"
              :disable-fields="false"
              @input="onPickerInput"
            />
            <div
              v-if="presets && presets.length"
              class="color-picker-thux__swatches"
              role="group"
              :aria-label="'Presets' | translate"
            >
              <button
                v-for="preset in presets"
                :key="preset"
                type="button"
                class="color-picker-thux__swatches-item"
                :class="{ 'is-active': isPresetActive(preset) }"
                :style="{ backgroundColor: preset }"
                :title="preset"
                @click.prevent="applyPreset(preset)"
              ></button>
            </div>
          </div>
        </b-popover>
      </div>
    </template>
  </form-group-thux-horizontal>
</template>

<script>
import { Chrome } from 'vue-color'

export default {
  name: 'form-color-picker-thux',
  components: { 'chrome-picker': Chrome },
  props: {
    validator: {},
    labelForm: {},
    labelColsSm: { default: 2 },
    labelColsLg: { default: 2 },
    labelFormClass: {},
    classForm: {},
    externalErrors: {},
    description: {},
    disabled: { default: false },
    value: { required: true },
    placeholderInput: {},
    presets: {
      type: Array,
      default: () => [
        '#00acac', '#348fe2', '#8753de', '#f59c1a',
        '#ff5b57', '#32a932', '#2d353c', '#b6c2c9'
      ]
    }
  },
  data () {
    return {
      model: undefined,
      popoverShown: false,
      triggerId: `color-picker-thux-${this._uid}`
    }
  },
  computed: {
    swatchStyle () {
      return this.isValidHex(this.model)
        ? { backgroundColor: this.model, backgroundImage: 'none' }
        : {}
    },
    pickerValue () {
      return this.isValidHex(this.model) ? this.model : '#cccccc'
    }
  },
  watch: {
    value () {
      this.$set(this, 'model', this.normalize(this.value))
    },
    model () {
      this.$emit('change', this.model)
    },
    popoverShown (val) {
      if (val) {
        document.addEventListener('mousedown', this.handleOutsideClick, true)
        document.addEventListener('keydown', this.handleEscape, true)
      } else {
        document.removeEventListener('mousedown', this.handleOutsideClick, true)
        document.removeEventListener('keydown', this.handleEscape, true)
      }
    }
  },
  created () {
    this.$set(this, 'model', this.normalize(this.value))
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleOutsideClick, true)
    document.removeEventListener('keydown', this.handleEscape, true)
  },
  methods: {
    normalize (v) {
      if (!v) return v
      const s = String(v).trim().toLowerCase()
      return s.startsWith('#') ? s : `#${s}`
    },
    isValidHex (v) {
      return typeof v === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)
    },
    isPresetActive (hex) {
      return this.isValidHex(this.model) && this.model.toLowerCase() === hex.toLowerCase()
    },
    formatHex (v) {
      if (!v) return v
      let s = String(v).trim().toLowerCase().replace(/[^#0-9a-f]/g, '')
      if (!s.startsWith('#')) s = `#${s}`
      return s.slice(0, 7)
    },
    togglePopover () {
      if (this.disabled) return
      this.popoverShown = !this.popoverShown
    },
    onPickerInput (payload) {
      if (!payload) return
      const hex = (payload.hex || '').toLowerCase()
      if (hex) this.$set(this, 'model', hex)
    },
    applyPreset (hex) {
      this.$set(this, 'model', hex.toLowerCase())
    },
    clear () {
      this.$set(this, 'model', '')
    },
    handleOutsideClick (e) {
      const root = this.$el
      if (root && root.contains(e.target)) return
      const pop = document.querySelector('.color-picker-thux__popover')
      if (pop && pop.contains(e.target)) return
      this.popoverShown = false
    },
    handleEscape (e) {
      if (e.key === 'Escape') this.popoverShown = false
    }
  }
}
</script>
